<template>
  <div class="inquiry-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Reconciliation Fees</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalRecords }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="headerBranch" class="text-muted"> - {{ headerBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Reconciliation Fees</span>
              <span v-if="dateRangeLabel" class="date-range-text mr-2">
                <strong>{{ dateRangeLabel }}</strong>
              </span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!campusGroups.length" class="empty-state">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No records found for the selected criteria</p>
      </div>

      <section
        v-for="(campus, campusIndex) in campusGroups"
        :key="campus.key"
        class="campus-block"
      >
        <div class="campus-heading">
          <div class="campus-heading__left">
            <span class="campus-heading__index">{{ campusIndex + 1 }}</span>
            <span class="campus-heading__name">{{ campus.name }}</span>
          </div>
          <span class="campus-heading__count">{{ campus.records.length }} records</span>
        </div>

        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 4%">S.NO</th>
                <th class="text-center" style="width: 8%">GR. NO</th>
                <th class="text-left" style="width: 14%">NAME</th>
                <th class="text-left" style="width: 14%">FATHER NAME</th>
                <th class="text-center" style="width: 10%">DATE OF ADMISSION</th>
                <th class="text-center" style="width: 8%">FEES</th>
                <th class="text-center" style="width: 10%">MONTH OF WITHDRAWAL</th>
                <th class="text-center" style="width: 10%">MONTH OF TRANSFER</th>
                <th class="text-center" style="width: 10%">FREE SHIP STUDENT</th>
                <th class="text-center" style="width: 12%">STATUS</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in campus.records"
                :key="campus.key + '-' + index"
              >
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-center">{{ row.grNo || "-" }}</td>
                <td class="text-left font-weight-bold">{{ displayName(row.name) }}</td>
                <td class="text-left">{{ displayName(row.fatherName) }}</td>
                <td class="text-center">{{ formatDate(row.date_of_admission) }}</td>
                <td class="text-center">{{ formatFees(row.fees) }}</td>
                <td class="text-center">{{ row.monthOfWithdrawal || "-" }}</td>
                <td class="text-center">{{ row.monthOfTransfer || "-" }}</td>
                <td class="text-center">{{ row.freeShipStudent || "-" }}</td>
                <td class="text-center">{{ row.reportStatus || "-" }}</td>
              </tr>
              <tr v-if="!campus.records.length">
                <td colspan="10" class="text-center py-4 no-data">
                  No records in this campus
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ReconciliationFeesPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      campusGroups: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      selectedCampusID: 0,
      departmentID: 0,
      classID: 0,
      sectionID: 0,
      reportStatus: "all",
      dateFrom: null,
      dateTill: null,
    };
  },
  computed: {
    isAllCampusesView() {
      return Number(this.selectedCampusID) === 1 || Number(this.cID) === 0;
    },
    headerBranch() {
      return this.isAllCampusesView ? "All Campuses" : this.campusBranch || "";
    },
    totalRecords() {
      return this.campusGroups.reduce(
        (sum, campus) => sum + (campus.records?.length || 0),
        0
      );
    },
    dateRangeLabel() {
      if (this.dateFrom && this.dateTill) {
        return `${this.formatDate(this.dateFrom)} to ${this.formatDate(this.dateTill)}`;
      }
      if (this.dateFrom) return `From ${this.formatDate(this.dateFrom)}`;
      if (this.dateTill) return `Till ${this.formatDate(this.dateTill)}`;
      return "";
    },
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.selectedCampusID = Number(
        query.selectedCampusID || query.campusID || query.cID || 0
      );
      this.cID = Number(query.cID || 0);
      this.departmentID = Number(query.departmentID || 0);
      this.classID = Number(query.classID || 0);
      this.sectionID = Number(query.sectionID || 0);
      this.reportStatus = query.reportStatus || "all";
      this.dateFrom = query.dateFrom || query.dtFrom || null;
      this.dateTill = query.dateTill || query.dtTo || null;

      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const branches = this.$store.state.branches || [];
        const lookupId = this.isAllCampusesView
          ? this.$store.state.userData.cId
          : this.selectedCampusID || this.cID;
        const currentBranch =
          branches.find((b) => Number(b.id) === Number(lookupId)) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = this.isAllCampusesView
            ? "All Campuses"
            : currentBranch.branch || currentBranch.branchName || "";
          this.schoolLogo = currentBranch.logo;
        } else if (this.isAllCampusesView) {
          this.campusBranch = "All Campuses";
        }
      } catch (e) {
        console.warn("Campus info fetch failed", e);
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          db: this.db,
          cID: Number(this.cID) || 0,
          classID: Number(this.classID) || 0,
          sectionID: Number(this.sectionID) || 0,
          departmentID: Number(this.departmentID) || 0,
          reportStatus: this.reportStatus || "all",
        };

        if (this.dateFrom) params.dateFrom = this.dateFrom;
        if (this.dateTill) params.dateTill = this.dateTill;

        const url = `${this.$store.state.domain}Students/ReconciliationRequirementReport`;
        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const payload = this.extractPayload(response.data);
        this.campusGroups = this.normalizeCampusGroups(payload);
      } catch (err) {
        console.error("Reconciliation fees report error:", err);
        this.error =
          err?.response?.data?.message ||
          "Server error while fetching reconciliation fees report.";
        this.campusGroups = [];
      } finally {
        this.loading = false;
      }
    },
    extractPayload(data) {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (data.status === "success" && data.data != null) return data.data;
      if (Array.isArray(data.data)) return data.data;
      if (data.data && typeof data.data === "object") return data.data;
      return data;
    },
    normalizeRecord(raw) {
      const freeShip =
        raw.freeShipStudent ??
        raw.free_ship_student ??
        raw.isFreeShip ??
        raw.freeShip;
      let freeShipLabel = "-";
      if (freeShip === true || freeShip === 1 || freeShip === "1") {
        freeShipLabel = "YES";
      } else if (freeShip === false || freeShip === 0 || freeShip === "0") {
        freeShipLabel = "NO";
      } else if (freeShip != null && freeShip !== "") {
        freeShipLabel = String(freeShip).toUpperCase();
      }

      return {
        sno: raw.sno,
        grNo: raw.grNo || raw.gr || raw.GRNo || "",
        name: raw.name || raw.studentName || "",
        fatherName:
          raw.fatherName || raw.father_name || raw.father || "",
        date_of_admission:
          raw.date_of_admission ||
          raw.dateOfAdmission ||
          raw.doa ||
          null,
        fees: raw.fees ?? raw.monthly_fee ?? raw.monthlyFee ?? "",
        monthOfWithdrawal: raw.monthOfWithdrawal || raw.month_of_withdrawal || "",
        monthOfTransfer: raw.monthOfTransfer || raw.month_of_transfer || "",
        freeShipStudent: freeShipLabel,
        reportStatus: raw.reportStatus || raw.status || raw.STATUS || "",
        campusID: raw.campusID,
        campusName:
          raw.campusBranch ||
          raw.campusName ||
          raw.branch ||
          raw.campus ||
          "",
      };
    },
    looksLikeRecord(item) {
      if (!item || typeof item !== "object") return false;
      return (
        item.grNo != null ||
        item.name != null ||
        item.sno != null ||
        item.reportStatus != null
      );
    },
    normalizeCampusGroups(payload) {
      if (!payload) return [];

      if (Array.isArray(payload)) {
        if (!payload.length) return [];

        if (this.looksLikeRecord(payload[0])) {
          return this.groupFlatRecords(payload);
        }

        if (
          payload[0].records ||
          payload[0].items ||
          payload[0].students ||
          payload[0].campusName ||
          payload[0].campusID != null
        ) {
          return payload
            .map((campus, index) => this.normalizeCampus(campus, index))
            .filter((c) => c.records.length);
        }
      }

      if (typeof payload === "object") {
        if (Array.isArray(payload.campuses)) {
          return this.normalizeCampusGroups(payload.campuses);
        }
        if (Array.isArray(payload.records) || Array.isArray(payload.items)) {
          return this.groupFlatRecords(payload.records || payload.items);
        }
      }

      return [];
    },
    normalizeCampus(campus, index) {
      const name =
        campus.campusBranch ||
        campus.campusName ||
        campus.branch ||
        campus.name ||
        this.getCampusNameById(campus.campusID) ||
        `Campus ${index + 1}`;

      const rawRecords =
        campus.records ||
        campus.items ||
        campus.students ||
        campus.data ||
        [];

      const records = (Array.isArray(rawRecords) ? rawRecords : []).map((row) =>
        this.normalizeRecord(row)
      );

      return {
        key: `campus-${campus.campusID ?? index}-${name}`,
        campusID: campus.campusID,
        name,
        records,
      };
    },
    groupFlatRecords(records) {
      const campusMap = {};
      const campuses = [];

      records.forEach((raw) => {
        const row = this.normalizeRecord(raw);
        const campusID = raw.campusID != null ? raw.campusID : row.campusID;
        const campusName =
          row.campusName ||
          this.getCampusNameById(campusID) ||
          this.campusBranch ||
          this.campusName ||
          "Unknown Campus";
        const campusKey =
          campusID != null && campusID !== ""
            ? `id-${campusID}`
            : `name-${campusName}`;

        if (!campusMap[campusKey]) {
          campusMap[campusKey] = {
            key: campusKey,
            campusID,
            name: campusName,
            records: [],
          };
          campuses.push(campusMap[campusKey]);
        }

        campusMap[campusKey].records.push(row);
      });

      if (campuses.length <= 1 && !this.isAllCampusesView) {
        return campuses.length
          ? campuses
          : [
              {
                key: "single-campus",
                campusID: this.cID,
                name: this.campusBranch || this.campusName,
                records: records.map((raw) => this.normalizeRecord(raw)),
              },
            ];
      }

      return campuses.sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, {
          sensitivity: "base",
        })
      );
    },
    getCampusNameById(id) {
      if (id == null || id === "") return "";
      const match = (this.$store.state.branches || []).find(
        (b) => Number(b.id) === Number(id)
      );
      return match ? match.branch || match.name || "" : "";
    },
    displayName(value) {
      const text = (value || "-").toString().trim();
      return text ? text.toUpperCase() : "-";
    },
    formatFees(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isFinite(num)) return num.toLocaleString();
      return String(value);
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return String(dateStr).slice(0, 10);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.inquiry-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
}

.report-header-modern {
  border-bottom: 3px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 100px;
    width: auto;
  }

  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #636e72;
}

.campus-block {
  margin-bottom: 1.75rem;
}

.campus-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 0.8rem;
    font-weight: 800;
  }

  &__name {
    font-size: 1rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
  }

  &__count {
    font-size: 0.82rem;
    font-weight: 700;
    color: #fff;
    background: #2c3e50;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 8px;
    font-size: 0.82rem;
  }

  thead {
    background: #2c3e50;

    th {
      color: white;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.75rem;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
}

.no-data {
  color: #adb5bd;
}

@media print {
  .no-print {
    display: none !important;
  }

  .inquiry-report {
    background: white;
    padding: 0;
  }

  .report-page {
    box-shadow: none;
    border: none;
    padding: 0.5cm;
    width: 100%;
    max-width: 100%;
    margin: 0;
  }

  .ledger-style-table {
    th,
    td {
      padding: 5px 6px;
      border: 1px solid #000 !important;
      color: #000;
      font-size: 0.7rem;
    }

    thead th {
      background: #eee !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
}
</style>
